import {useEffect, useRef, useState} from 'react'
import {
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Button,
  Link
} from '@mui/material'
import {faqData} from './faqJson'
import {useTheme} from '@mui/material/styles'
import TableOfContents from './tableofcontent'
import SearchFAQ from './searchFAQ'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {getPrismProgram} from '../../shared/helpers/common'

const FaqAccordion = () => {
  const [isAccordionActive, setIsAccordionActive] = useState(null)
  const [searchTerm, setSearchTerm] = useState()
  const [isAccordionTitleChange, setIsAccordionTitleChange] = useState(null)
  const [expandedAccordion, setExpandedAccordion] = useState('')
  const [isActive, setIsActive] = useState(null)
  const accordionRefs = useRef([])
  const containerRef = useRef(null)
  const theme = useTheme()
  const prismProgram = getPrismProgram()

  const accordionStyle = {
    borderRadius: '6px',
    border: 'none',
    boxShadow: 'none',
    '::before': {
      backgroundColor: 'transparent'
    }
  }

  const handleAccordionTitleChange = (id) => {
    setIsAccordionTitleChange(id)
  }

  const sectionTitles = [
    {range: [0, 8], title: `${prismProgram} Data`},
    {range: [9, 11], title: `${prismProgram} Models`},
    {range: [12, 13], title: 'Term Definitions'},
    {range: [14, 14], title: 'Map Information'},
    {range: [15, 19], title: 'Example Use Cases'},
    {range: [20, 22], title: 'Persona Feature'}
  ]

  const setAccordionTitle = (currentSectionId) => {
    for (const section of sectionTitles) {
      if (currentSectionId >= section.range[0] && currentSectionId <= section.range[1]) {
        setExpandedAccordion(section.title)
        break
      }
    }
  }

  const [isBackToTopVisible, setIsBackToTopVisible] = useState(false)

  const handleScroll = () => {
    const sections = containerRef.current.querySelectorAll('.faq-section')
    const container = containerRef.current
    const containerTop = container.scrollTop
    const windowTop = globalThis.scrollY
    const windowBottom = windowTop + containerRef.current.clientHeight
    let currentSectionId = null
    for (const section of sections) {
      const sectionTop = section.offsetTop

      if (windowBottom / 2 > sectionTop - containerTop) {
        currentSectionId = Number.parseInt(section.id, 10)
      }
    }
    if (currentSectionId !== null) {
      setIsActive(currentSectionId)
      setAccordionTitle(currentSectionId)
      setIsAccordionTitleChange('')
      setIsAccordionActive(currentSectionId)
    }
    setIsBackToTopVisible(windowTop + globalThis.innerHeight >= document.documentElement.scrollHeight - 5)
  }
  useEffect(() => {
    const container = containerRef.current

    const handleKeyDown = (event) => {
      if (event.keyCode === 38 || event.keyCode === 40) {
        handleScroll()
      }
    }
    container.addEventListener('keydown', handleKeyDown)
    container.addEventListener('wheel', handleScroll)
    globalThis.addEventListener('scroll', handleScroll)

    return () => {
      container.removeEventListener('keydown', handleKeyDown)
      container.removeEventListener('wheel', handleScroll)
      globalThis.removeEventListener('scroll', handleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scrollToTop = () => {
    handleItemClick(`${prismProgram} Data`)
    setTimeout(() => {
      globalThis.scrollTo({top: 0, behavior: 'smooth'})
    }, 1500)
    setIsAccordionTitleChange(`${prismProgram} Data`)
    setExpandedAccordion('')
  }

  const handleItemClick = (index) => {
    setIsActive('')
    setIsAccordionActive(index)
    const element = accordionRefs.current[index]
    const container = containerRef.current

    if (element && container) {
      setTimeout(() => {
        const elementTop = element.offsetTop - container.offsetTop
        container.scrollTo({top: elementTop, behavior: 'smooth'})
      }, 500)
    }
  }
  const renderBulletPoints = (bullet) => {
    return bullet.points.map((bullets) => (
      <li key={bullet.id} style={{marginLeft: '48px'}} dangerouslySetInnerHTML={{__html: bullets}} />
    ))
  }

  const renderSubDescription = (subdescription) => {
    if (Array.isArray(subdescription)) {
      return subdescription.map((desc) => <span key={desc} dangerouslySetInnerHTML={{__html: desc}} />)
    }
    return <span dangerouslySetInnerHTML={{__html: subdescription}} />
  }

  return (
    <Grid>
      <SearchFAQ
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleItemClick={handleItemClick}
        setIsAccordionTitleChange={setIsAccordionTitleChange}
        setIsAccordionActive={setIsAccordionActive}
      />
      <Grid container md={12} lg={12} sx={{display: 'flex', position: 'relative'}} data-testid='faq-accordion'>
        <Grid item xs={12} md={3} lg={3} sx={{flex: 'none'}}>
          <TableOfContents
            handleItemClick={handleItemClick}
            isAccordionActive={isAccordionActive}
            setIsAccordionActive={setIsAccordionActive}
            handleAccordionTitleChange={handleAccordionTitleChange}
            isAccordionTitleChange={isAccordionTitleChange}
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
            setIsActive={setIsActive}
            isActive={isActive}
          />
        </Grid>
        <Grid item xs={12} md={9} lg={9} sx={{overflowY: 'auto'}} className='accordion'>
          <Grid Container sx={{display: 'flex'}}>
            <Grid
              item
              ref={containerRef}
              sx={{
                maxHeight: 'calc(100vh + 30px)',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                padding: 0
              }}
            >
              {Object.keys(faqData).map((title) => (
                <Box key={title} sx={{marginBottom: '24px'}}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography
                      fontSize={theme.typography.h6.fontSize}
                      fontWeight={theme.typography.fontWeightSemibold}
                      color={theme.palette.hpAccentBlack}
                      ref={(el) => (accordionRefs.current[title] = el)}
                      marginTop={'10px'}
                    >
                      {title}
                    </Typography>
                  </Box>

                  {faqData?.[title]?.Content?.map((item) => {
                    const isSpecialItem = item.id === 5 || item.id === 7
                    const description = isSpecialItem ? (
                      <p>
                        {item.description}{' '}
                        <Link
                          onClick={() => {
                            handleItemClick(3)
                          }}
                          sx={{cursor: 'pointer'}}
                        >
                          Where does the data in {prismProgram} come from?
                        </Link>
                      </p>
                    ) : (
                      <p>{item.description}</p>
                    )

                    return (
                      <Grid key={item.id} className='faq-section' id={item.id}>
                        <Accordion
                          data-testid='accordion-content'
                          id={item.id}
                          disableGutters
                          sx={accordionStyle}
                          tabIndex={0}
                          ref={(el) => (accordionRefs.current[item.id] = el)}
                          expanded={true}
                        >
                          <AccordionSummary
                            aria-controls='panel1a-content'
                            id='panel1a-header'
                            sx={{borderTop: 'none', padding: 0, flexDirection: 'row-reverse'}}
                          >
                            <Typography
                              fontSize={theme.typography.h6.fontSize}
                              fontWeight={theme.typography.fontWeightSemibold}
                              color={theme.palette.hpAccentBlack}
                              paddingLeft={'16px'}
                            >
                              {item.title}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography
                              fontWeight={theme.typography.fontWeightBold}
                              color={theme.palette.hpAccentBlack}
                              fontSize={theme.typography.h6.fontSize}
                            >
                              {item.question}
                            </Typography>
                            <Typography>
                              <ul
                                style={{
                                  color: theme.palette.hpAccentBlack,
                                  padding: 0,
                                  fontSize: theme.typography.h6.fontSize
                                }}
                              >
                                {item.description.length > 1
                                  ? item.description.map((bullet) => (
                                      <p key={bullet.id}>
                                        {renderSubDescription(bullet.subdescription)} {renderBulletPoints(bullet)}
                                      </p>
                                    ))
                                  : description}
                              </ul>
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                        <Divider />
                      </Grid>
                    )
                  })}
                </Box>
              ))}
            </Grid>
          </Grid>
          {isBackToTopVisible && (
            <Button
              data-testid='back-to-top'
              onClick={() => scrollToTop()}
              style={{
                position: 'absolute',
                bottom: '-28px',
                right: '0px',
                fontSize: theme.typography.subtitle1.fontSize,
                borderRadius: '5px',
                cursor: 'pointer',
                color: theme.palette.hpBaseDarker,
                background: theme.palette.hpWhite,
                textTransform: 'none',
                border: `1px solid ${theme.palette.hpBaseLight}`
              }}
            >
              <KeyboardArrowUpIcon />
              Back to Top
            </Button>
          )}
        </Grid>
        <Box sx={{height: '100px'}} />
      </Grid>
    </Grid>
  )
}

export default FaqAccordion

/* Function Descriptions:
  - FaqAccordion: A React component that displays a FAQ accordion with search and navigation functionalities.
  - handleAccordionTitleChange: Updates the state to track the currently active accordion title.
  - setAccordionTitle: Determines and sets the title of the expanded accordion based on the current section ID.
  - handleScroll: Handles scroll events to update active section, accordion title, and "Back to Top" visibility.
  - scrollToTop: Scrolls the page to the top and resets the accordion title state.
  - handleItemClick: Scrolls to a specific accordion section when clicked.
  - renderBulletPoints: Renders a list of bullet points for a given FAQ item.
  - useEffect: Sets up event listeners for scroll and keydown events to manage accordion behavior and visibility.
  - handleKeyDown: Handles keyboard navigation for the accordion.
*/
